import PropTypes from 'prop-types';
import React from 'react';

import ButtonPlay from '../icons/ButtonPlay';
import styleVariables from '../../stylesheets/_variables.scss';
import { nl2br } from '../../utils/text';

import './WistiaEmbed.scss';

const WistiaEmbed = ({
  autoplay, continueFromSeconds, episodes, filterBackground, hashedId, onEnd, sendStats, shadowed, silentAutoPlay, smallPlayButton, title, ...embedOptions
}) => {
  const videoOverlayId = `video-overlay-${hashedId}`;

  const handle = React.useRef(null);
  const progressInterval = React.useRef(null);
  const secondsWatched = React.useRef(0);
  const videoOverlay = React.useRef(null);

  const [lastProgressBeforeStats, setLastProgressBeforeStats] = React.useState(continueFromSeconds || 0);

  const fadeOut = (idSelector) => {
    const fadeTarget = document.getElementById(idSelector);
    fadeTarget.classList.add('fade');
  };

  const trackProgress = () => {
    if (handle.current) {
      const progress = Math.floor(handle.current.time());
      const duration = Math.floor(handle.current.duration());
      // const timeWatched = progress - lastProgressBeforeStats;

      if (!!progress && sendStats && secondsWatched.current > 0) {
        sendStats({
          progress,
          duration,
          timeWatched: secondsWatched.current,
          from: lastProgressBeforeStats,
          to: progress,
        });
      }

      secondsWatched.current = 0;

      setLastProgressBeforeStats(progress);
    }
  };

  const onVideoReady = () => {
    if (!silentAutoPlay) {
      handle.current.unmute();
    }

    // on play, seek the video to 10 seconds, then unbind so it
    // only happens once.
    handle.current.bind('play', () => {
      fadeOut(videoOverlayId);
    });

    handle.current.bind('end', () => {
      trackProgress();

      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }

      if (onEnd) {
        onEnd();
      }
    });

    // when the video is paused
    handle.current.bind('pause', () => {
      trackProgress();

      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    });

    // when the video time changes (gets "seconds" as parameter)
    handle.current.bind('secondchange', () => {
      secondsWatched.current += 1;

      if (!progressInterval.current) {
        progressInterval.current = setInterval(() => {
          trackProgress();
        }, progressInterval.current);
      }
    });

    if (videoOverlay.current) {
      videoOverlay.current.addEventListener('click', () => {
        handle.current.play();
        fadeOut(videoOverlayId);
      });
    }

    // when you click the custom play button
    /* document.getElementById(videoOverlayId).addEventListener('click', () => {
      console.log('click', videoOverlayId);
      video.play();
      this.fadeOut(videoOverlayId);
    }); */

    if (continueFromSeconds) {
      handle.current.time(continueFromSeconds);
    }

    if (autoplay) {
      handle.current.play();
    }

    /* if (episodes && episodes.length > 0) {
      episodes.forEach((episode) => {
        video.addToPlaylist(episode.slug);
      });
    } */
  };

  React.useEffect(() => {
    if (!document.getElementById('wistia_script')) {
      const wistiaScript = document.createElement('script');
      wistiaScript.id = 'wistia_script';
      wistiaScript.type = 'text/javascript';
      wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js';
      wistiaScript.async = true;
      document.body.appendChild(wistiaScript);
    }

    return () => {
      trackProgress();

      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }

      if (handle.current) {
        handle.current.remove();
        handle.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    window._wq = window._wq || [];
    window._wq.push({
      id: hashedId,
      options: {
        ...embedOptions,
        playerColor: styleVariables.colorTeak,
      },
      onHasData: (video) => {
        handle.current = video;
      },
      onReady: (video) => {
        handle.current = video;
        onVideoReady();
      },
    });
  }, [hashedId]);

  return (
    <div
      className={`wistia_responsive_padding ${shadowed && 'shadowed'}`}
      style={{
        padding: '56.25% 0 0 0', position: 'relative',
      }}
    >
      <div
        className="wistia_responsive_wrapper"
        style={{
          height: '100%', left: '0', position: 'absolute', top: '0', width: '100%',
        }}
      >
        <div className="video-overlay" id={`video-overlay-${hashedId}`} ref={videoOverlay}>
          <div className="video-filter" style={{ background: filterBackground }} />
          {title ? (
            <div className="title-wrapper">
              <ButtonPlay className="play-button" />
              <div className="card-title" dangerouslySetInnerHTML={{ __html: nl2br(title) }} />
            </div>
          ) : (
            <ButtonPlay className={`play-btn ${smallPlayButton ? 'small' : ''}`} id={`play-btn-${hashedId}`} />
          )}
        </div>
        <div
          className={`wistia_embed wistia_async_${hashedId} videoFoam=true`}
          style={{
            height: '100%', width: '100%',
          }}
        >
          &nbsp;
        </div>
      </div>
    </div>
  );
};

WistiaEmbed.propTypes = {
  continueFromSeconds: PropTypes.number,
  filterBackground: PropTypes.string,
  hashedId: PropTypes.string.isRequired,
  shadowed: PropTypes.bool,
  episodes: PropTypes.array,
  onEnd: PropTypes.func,
  sendStats: PropTypes.func,
  autoplay: PropTypes.bool,
  silentAutoPlay: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  progressInterval: PropTypes.number,
  smallPlayButton: PropTypes.bool,
  title: PropTypes.string,
};

WistiaEmbed.defaultProps = {
  continueFromSeconds: null,
  filterBackground: null,
  shadowed: false,
  episodes: null,
  onEnd: null,
  sendStats: null,
  autoplay: false,
  silentAutoPlay: false,
  progressInterval: 10,
  smallPlayButton: false,
  title: null,
};

export default WistiaEmbed;
